import { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { AppContext } from '../App'
import { Condition } from '../proto/generated/nexus'
import {
    Broker_OrderType,
    Broker_Variety,
    Order,
    OrderStatus,
    OrderType,
    Product,
    TransactionType,
    Validity,
} from '../proto/generated/order'
import { Security } from '../proto/generated/security'
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from '../proto/generated/trading-bridge'
import { setAlertModalData } from '../reducer/alerts.reducer'
import { openConnectAccountDialog, updateOrderHistory } from '../reducer/orders.reducer'
import { GroupedPosition } from '../reducer/positions.reducer'
import { ChartAlertSnapshot } from '../components/alerts/alertModal.typings'
import { selectActiveChartId } from '../selectors/chart'
import { selectOrdersHistory } from '../selectors/orders'
import { selectPrimaryTradingAccount, selectTradingAccounts } from '../selectors/user'
import store from '../store'
import { placeOrModifyOrder } from '../services/apis/orders'
import AnalyticsManager from '../analytics/AnalyticsManager'
import { addOrderAlertMessage } from '../util/orders'
import {
    buildQuickTradeChartSnapshot,
    hasConditionalRuleContent,
    isConditionalEntryReady,
    mergeQuickTradeSnapshotWithMatchingChart,
    orderExecutionParamsForConditionalEntry,
    resolveAuxiliaryLegBatchId,
    resolveAuxiliaryLegWireIds,
    userAlertForConditionalEntryModal,
} from '../util/conditionalEntry'
import { registerConditionalEntryAlertModalHandlers } from '../util/conditionalEntryAlertModalBridge'
import { getSecurityAsync, getSecurityKey } from '../util/security'
import { findStandaloneConditionalExitLeg, findStandaloneSquareOffEntryBatch } from '../util/quickTradeConditionalPlace'

export function useStandalonePositionConditionalExit() {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const orderHistory = useSelector(selectOrdersHistory)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const tradingAccounts = useSelector(selectTradingAccounts)

    const openConditionalExitModal = useCallback(
        async (params: {
            groupId: string
            security: Security
            product: Product
            standaloneLeg: NonNullable<GroupedPosition['children']>[number]
        }) => {
            const { groupId, security, product, standaloneLeg } = params

            if (!tradingAccounts?.length || primaryTradingAccount?.status !== TradingAccountStatus.ACTIVE) {
                AnalyticsManager.logEvent('Place Order Clicked')
                if (!tradingAccounts?.length) {
                    dispatch(openConnectAccountDialog({ type: 'connect', tradingAccountId: '' }))
                } else if (primaryTradingAccount) {
                    dispatch(
                        openConnectAccountDialog({
                            type: 'reconnect',
                            tradingAccountId: primaryTradingAccount.accountId,
                        })
                    )
                }
                return
            }

            const sk = getSecurityKey(security.exchange, security.code)
            const snap = store.getState().securityStore.snapshots[sk]
            const lastPrice = snap?.lastTradedPrice ?? 0
            const lastVolume = snap?.volume ?? 0

            const entryBatchId = findStandaloneSquareOffEntryBatch(orderHistory, groupId, security)
            const cxLeg = findStandaloneConditionalExitLeg(orderHistory, groupId, security, entryBatchId)

            const seedCondition = cxLeg?.orderExecutionParams?.condition
            const chartFn = appContext.getChartSnapshot[activeChartId]
            const chartSnap =
                typeof chartFn === 'function' ? (chartFn() as ChartAlertSnapshot | undefined) : undefined

            let merged: ChartAlertSnapshot
            const undCode = security.underlyingSecurityCode?.trim()
            const underlyingSec =
                undCode && security.exchange != null
                    ? await getSecurityAsync(getSecurityKey(security.exchange, undCode))
                    : undefined

            if (underlyingSec) {
                const underlyingBase = buildQuickTradeChartSnapshot(underlyingSec, 0, 0)
                merged = mergeQuickTradeSnapshotWithMatchingChart(
                    underlyingBase,
                    chartSnap,
                    underlyingSec
                )
            } else {
                const baseSnap = buildQuickTradeChartSnapshot(security, lastPrice, lastVolume)
                merged = mergeQuickTradeSnapshotWithMatchingChart(baseSnap, chartSnap, security)
            }

            const alert = userAlertForConditionalEntryModal(
                merged,
                hasConditionalRuleContent(seedCondition) ? seedCondition : undefined
            )

            const transactionType =
                standaloneLeg.quantity > 0 ? TransactionType.SELL : TransactionType.BUY
            const quantity = Math.abs(standaloneLeg.quantity)
            const conditionalExitClientOrderId = cxLeg?.clientOrderId ?? uuid()

            const placeCx = async (condition: Condition) => {
                if (!primaryTradingAccount?.accountId) return
                if (!isConditionalEntryReady({ enabled: true, condition })) return
                const exitParams = orderExecutionParamsForConditionalEntry(
                    cxLeg?.orderExecutionParams,
                    { enabled: true, condition },
                    { stripWhenDisabled: true }
                )
                if (!hasConditionalRuleContent(exitParams?.condition)) return

                const cxWire = resolveAuxiliaryLegWireIds({
                    existingLeg: cxLeg,
                    fallbackClientOrderId: conditionalExitClientOrderId,
                })
                const cxBatchId = resolveAuxiliaryLegBatchId({
                    orders: orderHistory,
                    basketId: groupId,
                    existingAuxLeg: cxLeg,
                    standaloneEntryBatchId: entryBatchId,
                })
                const orderWire: Order = {
                    security: { code: security.code, exchange: security.exchange },
                    transactionType,
                    orderType: OrderType.CONDITIONAL_EXIT,
                    brokerOrderType: Broker_OrderType.MARKET_ORDER,
                    quantity,
                    isBasket: false,
                    batchId: cxBatchId,
                    orderDate: Date.now(),
                    product,
                    price: 0,
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: cxWire.clientOrderId,
                    orderId: cxWire.orderId,
                    immediateExecutePaperOrder: false,
                    orderExecutionParams: exitParams ?? {},
                }

                const request: PlaceOrModifyOrdersRequest = {
                    tradingAccountId: primaryTradingAccount.accountId,
                    order: [orderWire],
                    buyBeforeSell: false,
                }

                try {
                    const response = await placeOrModifyOrder(request)
                    const updates: Order[] = []
                    let ok = 0
                    ;(response.orderResponse || []).forEach((orderResponse, i) => {
                        const { successful, errorMessage, order: respOrder } = orderResponse
                        if (!successful) {
                            addOrderAlertMessage({
                                ...request.order[i],
                                status: OrderStatus.FAILED,
                                message: errorMessage || 'Unable to place order',
                            })
                        } else {
                            ok++
                            if (respOrder) {
                                updates.push(respOrder)
                                addOrderAlertMessage({
                                    ...respOrder,
                                    message: 'Order placed successfully',
                                })
                            }
                        }
                    })
                    if (updates.length) dispatch(updateOrderHistory({ updates }))
                    if (ok === request.order.length) {
                        AnalyticsManager.logEvent('Conditional exit placed from positions')
                    }
                } catch (e: unknown) {
                    addOrderAlertMessage({
                        ...orderWire,
                        status: OrderStatus.FAILED,
                        message: e instanceof Error ? e.message : 'Unable to place order',
                    })
                }
            }

            registerConditionalEntryAlertModalHandlers(
                (condition: Condition) => {
                    void placeCx(condition)
                },
                () => {}
            )

            dispatch(
                setAlertModalData({
                    chartSnapshot: merged,
                    alert,
                    edit: false,
                    purpose: 'conditionalExit',
                    conditionalUseFullIndicatorCatalog: false,
                })
            )
        },
        [
            activeChartId,
            appContext.getChartSnapshot,
            dispatch,
            orderHistory,
            primaryTradingAccount,
            tradingAccounts,
        ]
    )

    return { openConditionalExitModal }
}
